import React from "react";
import {
  Mail,
  Linkedin,
  ExternalLink,
  Download,
  FileText,
  Trophy,
  BookOpen,
  Newspaper,
  Vote,
  Briefcase,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";

/* ============================================================
   Kapil Bhaskar — single-page portfolio
   Layout mirrors the sample PDF (hero band → REFERENCE WORK
   title → 2×3 grid of boxes → expanded detail sections → footer).
   Palette: Deep Burgundy + warm cream + gold accent.
============================================================ */

const T = {
  primary:   "#6B1F2C", // deep burgundy
  primary2:  "#8B2D40", // soft burgundy
  primaryDk: "#4E1620",
  accent:    "#C9A227", // warm gold
  accent2:   "#E8C56E",
  cream:     "#FBF8F3",
  cream2:    "#F3EEE1",
  surface:   "#FFFFFF",
  ink:       "#1A1A1A",
  text:      "#1F1F1F",
  text2:     "#4A4A4A",
  text3:     "#8A8A8A",
  border:    "#E6DFCF",
  borderDark: "#CBC2A6",
  display: "'Space Grotesk', 'Cabinet Grotesk', 'Inter', system-ui, sans-serif",
  body:    "'Inter', system-ui, sans-serif",
  mono:    "'JetBrains Mono', ui-monospace, monospace",
};

const Container = ({ children, style, className }) => (
  <div
    className={className}
    style={{ maxWidth: 1120, margin: "0 auto", padding: "0 24px", ...(style || {}) }}
  >
    {children}
  </div>
);

/* ============================================================
   HERO BAND — burgundy, white text block LEFT + photo RIGHT
============================================================ */
const Hero = () => (
  <section
    id="top"
    style={{
      backgroundColor: T.primary,
      backgroundImage: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDk} 100%)`,
      padding: "10px 0 12px",
      position: "relative",
    }}
  >
    <Container>
      <div
        className="hero-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 20,
          alignItems: "center",
        }}
      >
        {/* Left: compact white text block */}
        <div
          style={{
            backgroundColor: T.surface,
            padding: "10px 18px",
            borderRadius: 4,
            boxShadow: "0 14px 36px -22px rgba(0,0,0,0.4)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "4px 16px",
          }}
        >
          <div style={{ flex: "0 0 100%", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <h1
              style={{
                margin: 0,
                fontFamily: T.display,
                fontSize: "clamp(22px, 2.6vw, 30px)",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.05,
                color: T.ink,
              }}
            >
              Kapil Bhaskar
            </h1>
            <div
              style={{
                fontFamily: T.mono,
                fontSize: 10,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: T.accent,
                fontWeight: 600,
              }}
            >
              Statistics · Research · Civic Tech
            </div>
          </div>
          <p
            style={{
              margin: 0,
              fontFamily: T.body,
              fontSize: 12.5,
              lineHeight: 1.4,
              color: T.text2,
              flex: "1 1 auto",
            }}
          >
            Grade 12 · Vidya Mandir Senior Secondary School · Applying Fall 2027
          </p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 4, alignSelf: "center" }}>
            <a
              href="/kapil-bhaskar-resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-view-resume"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 14px",
                backgroundColor: T.accent,
                color: T.ink,
                fontFamily: T.display,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                textDecoration: "none",
                border: `2px solid ${T.accent}`,
                boxShadow: `0 3px 0 ${T.primaryDk}`,
                transition: "transform 180ms ease, box-shadow 180ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = `0 4px 0 ${T.primaryDk}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = `0 3px 0 ${T.primaryDk}`;
              }}
            >
              <Download size={12} /> View Résumé
            </a>
            <a
              href="https://www.linkedin.com/in/kapil-bhaskar-2572aa291/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              title="LinkedIn"
              data-testid="hero-linkedin"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: 999,
                border: `2px solid ${T.primary}`,
                color: T.primary,
                textDecoration: "none",
                transition: "transform 180ms ease, background-color 180ms ease, color 180ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = T.primary;
                e.currentTarget.style.color = T.surface;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = T.primary;
                e.currentTarget.style.transform = "none";
              }}
            >
              <Linkedin size={14} />
            </a>
            <a
              href="mailto:KapilBhaskar1451@gmail.com"
              aria-label="Email"
              title="KapilBhaskar1451@gmail.com"
              data-testid="hero-email"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: 999,
                border: `2px solid ${T.primary}`,
                color: T.primary,
                textDecoration: "none",
                transition: "transform 180ms ease, background-color 180ms ease, color 180ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = T.primary;
                e.currentTarget.style.color = T.surface;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = T.primary;
                e.currentTarget.style.transform = "none";
              }}
            >
              <Mail size={14} />
            </a>
          </div>
        </div>

        {/* Right: compact photo (visually larger, overflows the band) */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: -8,
            marginBottom: -8,
          }}
        >
          <div
            style={{
              padding: 4,
              borderRadius: 999,
              backgroundColor: T.cream,
              boxShadow: "0 14px 36px -14px rgba(0,0,0,0.5)",
            }}
          >
            <img
              src="/kapil-hero.jpg"
              alt="Kapil Bhaskar"
              data-testid="hero-portrait"
              style={{
                display: "block",
                width: 95,
                height: 95,
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ============================================================
   REFERENCE WORK — section title + rich illustrated tiles
============================================================ */
const SectionTitle = ({ eyebrow, children }) => (
  <div style={{ textAlign: "center", marginBottom: 10 }}>
    {eyebrow && (
      <div
        aria-hidden
        style={{
          width: 48,
          height: 2,
          backgroundColor: T.accent,
          margin: "0 auto 8px",
        }}
      />
    )}
    <h2
      style={{
        margin: 0,
        fontFamily: T.display,
        fontSize: "clamp(16px, 1.9vw, 20px)",
        fontWeight: 700,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: T.ink,
      }}
    >
      {children}
    </h2>
  </div>
);

/* --- Clean, minimal SVG mini-illustrations per tile (burgundy + gold) --- */
const Illus = {
  resume: (
    <svg viewBox="0 0 64 64" width="100%" height="100%" fill="none">
      <rect x="14" y="8" width="36" height="48" rx="2" stroke={T.primary} strokeWidth="2.2" fill={T.cream}/>
      <path d="M40 8 L50 18 L40 18 Z" fill={T.accent} stroke={T.primary} strokeWidth="2.2" strokeLinejoin="round"/>
      <line x1="20" y1="26" x2="42" y2="26" stroke={T.primary} strokeWidth="1.8" strokeLinecap="round" opacity="0.75"/>
      <line x1="20" y1="33" x2="38" y2="33" stroke={T.primary} strokeWidth="1.8" strokeLinecap="round" opacity="0.55"/>
      <line x1="20" y1="40" x2="42" y2="40" stroke={T.primary} strokeWidth="1.8" strokeLinecap="round" opacity="0.55"/>
      <line x1="20" y1="47" x2="34" y2="47" stroke={T.primary} strokeWidth="1.8" strokeLinecap="round" opacity="0.35"/>
    </svg>
  ),
  awards: (
    <svg viewBox="0 0 64 64" width="100%" height="100%" fill="none">
      <path d="M18 12 H46 V26 Q46 38 32 42 Q18 38 18 26 Z" fill={T.accent} stroke={T.primary} strokeWidth="2.2" strokeLinejoin="round"/>
      <path d="M18 16 Q10 18 12 26 Q13 30 18 30" stroke={T.primary} strokeWidth="2.2" strokeLinecap="round"/>
      <path d="M46 16 Q54 18 52 26 Q51 30 46 30" stroke={T.primary} strokeWidth="2.2" strokeLinecap="round"/>
      <rect x="27" y="42" width="10" height="5" fill={T.primary}/>
      <rect x="22" y="47" width="20" height="6" rx="1" fill={T.primary}/>
      <path d="M26 22 L31 27 L39 19" stroke={T.cream} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  research: (
    <svg viewBox="0 0 64 64" width="100%" height="100%" fill="none">
      <path d="M10 14 Q10 10 14 10 L30 10 Q32 11 32 14 L32 50 Q32 47 30 46 L14 46 Q10 46 10 50 Z" fill={T.cream} stroke={T.primary} strokeWidth="2.2" strokeLinejoin="round"/>
      <path d="M54 14 Q54 10 50 10 L34 10 Q32 11 32 14 L32 50 Q32 47 34 46 L50 46 Q54 46 54 50 Z" fill={T.cream} stroke={T.primary} strokeWidth="2.2" strokeLinejoin="round"/>
      <line x1="16" y1="18" x2="27" y2="18" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.55"/>
      <line x1="16" y1="24" x2="27" y2="24" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.4"/>
      <line x1="37" y1="18" x2="48" y2="18" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.55"/>
      <line x1="37" y1="24" x2="48" y2="24" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.4"/>
      <circle cx="42" cy="42" r="7" stroke={T.accent} strokeWidth="2.6" fill={T.surface}/>
      <line x1="47" y1="47" x2="54" y2="54" stroke={T.accent} strokeWidth="2.6" strokeLinecap="round"/>
    </svg>
  ),
  media: (
    <svg viewBox="0 0 64 64" width="100%" height="100%" fill="none">
      <rect x="8" y="14" width="40" height="38" rx="2" stroke={T.primary} strokeWidth="2.2" fill={T.cream}/>
      <path d="M48 22 H54 Q56 22 56 24 V50 Q56 52 54 52 Q50 52 48 50 Z" fill={T.accent} stroke={T.primary} strokeWidth="2.2" strokeLinejoin="round"/>
      <rect x="13" y="19" width="30" height="7" fill={T.primary}/>
      <line x1="13" y1="31" x2="37" y2="31" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.55"/>
      <line x1="13" y1="36" x2="40" y2="36" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.4"/>
      <rect x="13" y="41" width="12" height="8" fill={T.accent}/>
      <line x1="28" y1="43" x2="43" y2="43" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.5"/>
      <line x1="28" y1="48" x2="40" y2="48" stroke={T.primary} strokeWidth="1.6" strokeLinecap="round" opacity="0.4"/>
    </svg>
  ),
  neeyosi: (
    <svg viewBox="0 0 64 64" width="100%" height="100%" fill="none">
      <path d="M12 24 L32 10 L52 24 V50 Q52 52 50 52 H14 Q12 52 12 50 Z" fill={T.accent} stroke={T.primary} strokeWidth="2.2" strokeLinejoin="round"/>
      <rect x="27" y="34" width="10" height="18" fill={T.primary}/>
      <rect x="18" y="30" width="7" height="7" fill={T.cream} stroke={T.primary} strokeWidth="1.6"/>
      <rect x="39" y="30" width="7" height="7" fill={T.cream} stroke={T.primary} strokeWidth="1.6"/>
      <circle cx="32" cy="20" r="2.4" fill={T.primary}/>
    </svg>
  ),
  internships: (
    <svg viewBox="0 0 64 64" width="100%" height="100%" fill="none">
      <path d="M24 20 V16 Q24 13 27 13 H37 Q40 13 40 16 V20" stroke={T.primary} strokeWidth="2.2" strokeLinecap="round"/>
      <rect x="8" y="20" width="48" height="32" rx="2" stroke={T.primary} strokeWidth="2.2" fill={T.cream}/>
      <rect x="8" y="32" width="48" height="3" fill={T.accent}/>
      <rect x="29" y="29" width="6" height="9" rx="1" fill={T.accent} stroke={T.primary} strokeWidth="1.6"/>
    </svg>
  ),
};

const TILES = [
  { id: "resume",     label: "Résumé",          illus: Illus.resume,      anchor: "/kapil-bhaskar-resume.pdf", external: true, desc: "Full academic & activity CV (PDF)" },
  { id: "awards",     label: "Awards",          illus: Illus.awards,      anchor: "#awards",                                   desc: "Honours, scholarships & recognitions" },
  { id: "research",   label: "Research Papers", illus: Illus.research,    anchor: "#research",                                 desc: "Prof-guided papers in Statistics & Civic-tech" },
  { id: "media",      label: "Media Coverage",  illus: Illus.media,       anchor: "#media",                                    desc: "Features in national & regional press" },
  { id: "neeyosi",    label: "NeeYosi.com",     illus: Illus.neeyosi,     anchor: "#neeyosi",                                  desc: "Civic-technology platform I founded" },
  { id: "internships",label: "Internships",     illus: Illus.internships, anchor: "#internships",                              desc: "Hands-on roles across sport, policy & tech" },
];

const Tile = ({ t }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <a
      href={t.anchor}
      target={t.external ? "_blank" : undefined}
      rel={t.external ? "noopener noreferrer" : undefined}
      data-testid={`tile-${t.id}`}
      className="ref-tile"
      aria-label={`${t.label} — ${t.desc}`}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 12px",
        aspectRatio: "1 / 0.42",
        backgroundColor: T.surface,
        backgroundImage: `linear-gradient(180deg, ${T.surface} 0%, ${T.cream2} 100%)`,
        border: `1px solid ${hover ? T.accent : T.border}`,
        borderRadius: 4,
        textDecoration: "none",
        color: T.ink,
        overflow: "hidden",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover ? "0 16px 36px -14px rgba(107, 31, 44, 0.3)" : "none",
        transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
    >
      <div
        className="ref-tile-illus"
        style={{
          width: "22%",
          maxWidth: 42,
          aspectRatio: "1 / 1",
          marginBottom: 6,
          transform: hover ? "scale(0.9)" : "none",
          opacity: hover ? 0 : 1,
          transition: "transform 220ms ease, opacity 180ms ease",
        }}
      >
        {t.illus}
      </div>
      <span
        className="ref-tile-label"
        style={{
          fontFamily: T.display,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: T.ink,
          textAlign: "center",
          opacity: hover ? 0 : 1,
          transition: "opacity 180ms ease",
        }}
      >
        {t.label}
      </span>
      <span
        className="ref-tile-desc"
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "10px 16px",
          fontFamily: T.body,
          fontSize: 12.5,
          lineHeight: 1.35,
          color: T.primary,
          fontWeight: 500,
          textAlign: "center",
          opacity: hover ? 1 : 0,
          transform: hover ? "scale(1)" : "scale(0.98)",
          transition: "opacity 200ms ease 40ms, transform 220ms ease 40ms",
          pointerEvents: "none",
        }}
      >
        {t.desc}
      </span>
    </a>
  );
};

const ReferenceGrid = () => (
  <section style={{ padding: "8px 0 14px", backgroundColor: T.cream }}>
    <Container style={{ maxWidth: 840 }} className="ref-container">
      <SectionTitle eyebrow>Reference Work</SectionTitle>
      <div
        className="ref-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
        }}
        data-testid="reference-grid"
      >
        {TILES.map((t) => <Tile key={t.id} t={t} />)}
      </div>
    </Container>
  </section>
);

/* ============================================================
   DETAIL SECTIONS — scroll targets from the tiles
============================================================ */

const DetailSection = ({ id, title, children, bg = T.surface }) => (
  <section
    id={id}
    style={{ padding: "80px 0", backgroundColor: bg, borderTop: `1px solid ${T.border}` }}
  >
    <Container>
      <div
        style={{
          display: "inline-block",
          marginBottom: 32,
          borderBottom: `3px solid ${T.accent}`,
          paddingBottom: 8,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: T.display,
            fontSize: "clamp(26px, 3vw, 34px)",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: T.ink,
          }}
        >
          {title}
        </h2>
      </div>
      {children}
    </Container>
  </section>
);

/* --- Résumé detail --- */
const ResumeDetail = () => (
  <DetailSection id="resume" title="Résumé">
    <p
      style={{
        margin: 0,
        maxWidth: 720,
        fontFamily: T.body,
        fontSize: 16,
        lineHeight: 1.7,
        color: T.text2,
      }}
    >
      A single-page résumé summarising coursework, standardised tests, research
      with the University of Cambridge, IIT Madras, Chiron Labs and Krea
      University, internships, honours, and leadership.
    </p>
    <a
      href="/kapil-bhaskar-resume.pdf"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="resume-download-link"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        marginTop: 24,
        padding: "12px 22px",
        backgroundColor: T.primary,
        color: T.surface,
        fontFamily: T.display,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        textDecoration: "none",
        borderRadius: 2,
      }}
    >
      <Download size={14} /> Open PDF
    </a>
  </DetailSection>
);

/* --- Awards detail --- */
const AWARDS = [
  "All India Rank 1, CBSE Grade 10 Board Exams in Science (100%)",
  "Indian Statistical Institute LIMIT 2026, 2nd Place, Category B",
  "Lodha Genius Programme Scholar, full scholarship awarded by Ashoka University",
  "Gold Crest Award — Need-based allocation of Anganwadi childcare centres in Villupuram (Chiron Labs)",
  "Force for Good Hero nominee, The Better India × Aditya Birla Group",
  "1st Place, PS Aurix District-Level Science Fair, consecutive years",
  "Government commendation for serving as a CBSE board-exam scribe for students with cerebral palsy and autism",
];

const AwardsDetail = () => (
  <DetailSection id="awards" title="Awards" bg={T.cream}>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 14,
      }}
    >
      {AWARDS.map((a, idx) => (
        <div
          key={a}
          style={{
            display: "flex",
            gap: 16,
            padding: "18px 22px",
            backgroundColor: T.surface,
            border: `1px solid ${T.border}`,
            borderLeft: `4px solid ${T.accent}`,
            borderRadius: 2,
          }}
        >
          <span
            style={{
              fontFamily: T.mono,
              fontSize: 12,
              color: T.primary,
              fontWeight: 700,
              flexShrink: 0,
              paddingTop: 2,
            }}
          >
            {String(idx + 1).padStart(2, "0")}
          </span>
          <span
            style={{
              fontFamily: T.body,
              fontSize: 15,
              lineHeight: 1.55,
              color: T.text,
            }}
          >
            {a}
          </span>
        </div>
      ))}
    </div>
  </DetailSection>
);

/* --- Research Papers detail --- */
const RESEARCH = [
  {
    org: "Chiron Labs",
    when: "Dec 2025 – Aug 2026",
    title: "Need-based allocation of Anganwadi childcare centres in Villupuram",
    body: "Modelled childcare demand across all 13 CD Blocks from 2011 Census data. Closed-form sensitivity analysis. Sole author; submitted to NHSJS.",
    mentor: "Dr. Mudita Upmanyu, Chiron Labs · Co-Founder",
    highlight: "★ Gold Crest Award",
  },
  {
    org: "University of Cambridge",
    when: "Aug 2025 – Jul 2026",
    title: "Bayesian cross-learning for household electricity forecasting under extreme data scarcity",
    body: "Hierarchical Bayesian autoregressive models in Python and Stan on 2.07M minute-level observations. Partial pooling reduced MAPE from 78.97% → 61.71%. Submitted to international journals.",
    mentor: "Dr. Maharshi Dhada, University of Cambridge · Research Fellow",
  },
  {
    org: "IIT Madras",
    when: "Apr 2026",
    title: "Fine-scale urban air pollution in Chennai",
    body: "Analysed 37,769 mobile-sensor PM2.5 readings. Built a QC pipeline, zoned routes urban–semi-urban–rural, computed 15-min lagged autocorrelation (r = 0.781).",
    mentor: "Dr. Nikhil Bugalia, IIT Madras · Civil Engineering",
  },
  {
    org: "Sapien Labs, Krea University",
    when: "Dec 2025 – Mar 2026",
    title: "Lifestyle and mental health across 102,000+ Indian respondents",
    body: "OLS associations between sleep, exercise, ultra-processed food and Mental Health Quotient with HC3 robust SEs. Inadequate sleep associated with a ~56-point MHQ deficit.",
    mentor: "Dr. Shailender Swaminathan, Sapien Labs, Krea University · Director",
  },
];

const ResearchDetail = () => (
  <DetailSection id="research" title="Research Papers">
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
        gap: 20,
      }}
    >
      {RESEARCH.map((r) => (
        <article
          key={r.title}
          style={{
            padding: 24,
            backgroundColor: T.surface,
            border: `1px solid ${T.border}`,
            borderTop: `3px solid ${T.primary}`,
            borderRadius: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <span
              style={{
                fontFamily: T.mono,
                fontSize: 11,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: T.primary,
                fontWeight: 700,
              }}
            >
              {r.org} · {r.when}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: 2,
                backgroundColor: "#DCFCE7",
                color: "#166534",
                fontFamily: T.mono,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              <CheckCircle2 size={11} /> Completed
            </span>
            {r.highlight && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 2,
                  backgroundColor: T.accent2,
                  color: T.primaryDk,
                  fontFamily: T.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {r.highlight}
              </span>
            )}
          </div>
          <h3
            style={{
              margin: 0,
              fontFamily: T.display,
              fontSize: 19,
              fontWeight: 600,
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
              color: T.ink,
            }}
          >
            {r.title}
          </h3>
          <p
            style={{
              margin: "12px 0 0",
              fontFamily: T.body,
              fontSize: 14,
              lineHeight: 1.6,
              color: T.text2,
            }}
          >
            {r.body}
          </p>
          <div
            style={{
              marginTop: 14,
              paddingTop: 14,
              borderTop: `1px dashed ${T.border}`,
              fontFamily: T.body,
              fontSize: 13,
              color: T.text2,
              fontWeight: 500,
            }}
          >
            <span
              style={{
                display: "block",
                fontFamily: `"Playfair Display", "Cormorant Garamond", Georgia, serif`,
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 11.5,
                letterSpacing: "0.02em",
                color: T.text2,
                opacity: 0.8,
                marginBottom: 2,
              }}
            >
              under the guidance of
            </span>
            {r.mentor}
          </div>
        </article>
      ))}
    </div>
  </DetailSection>
);

/* --- Media Coverage detail --- */
const MEDIA = [
  { brand: "The Hindu BusinessLine", title: "AI-powered NeeYosi.com puts candidate info at your fingertips", href: "https://www.thehindubusinessline.com/info-tech/ai-powered-neeyosicom-puts-candidate-info-at-your-fingertips/article70893116.ece" },
  { brand: "NDTV", title: "How a Teen's Website Helped Tamil Nadu Voters Know About Candidates", href: "https://www.ndtv.com/india-news/how-a-teens-website-helped-tamil-nadu-voters-know-about-candidates-11405816" },
  { brand: "NDTV · Video Interview", title: "Tamil Nadu Votes Today: Gen Z Voices & NeeYosi Buzz", href: "https://www.ndtv.com/videos/tamil-nadu-votes-today-5-6-crore-electors-women-lead-gen-z-voices-neeyosi-buzz-1089011" },
  { brand: "TechStory", title: "Chennai-based NeeYosi.com crosses 20,000 users within 24 hours of launch", href: "https://techstory.in/chennai-based-website-neeyosi-com-crosses-20000-users-within-24-hours-of-launch/" },
  { brand: "The Better India", title: "This 16-YO Tech Whiz Built an AI Tool That Guided 70,000 Voters in Tamil Nadu", href: "https://thebetterindia.com/innovation/chennai-teen-ai-nee-yosi-tamil-nadu-voters-candidate-records-11862335" },
  { brand: "Mylapore Times", title: "Vidya Mandir's Kapil Bhaskar builds voter info platform; people are checking it out", href: "https://www.mylaporetimes.com/2026/04/vidya-mandirs-kapil-bhaskar-builds-voter-info-platform-users-flowing-in-on-polling-eve/" },
  { brand: "YOCee", title: "Vote Informed: Kapil Bhaskar's website provides access to all the information of election candidates", href: "https://yocee.in/neeyosi-a-youths-call-for-informed-voting" },
  { brand: "Tell My Story", title: "Nee Yosi Effect: How a Chennai Teen Helped Voters Navigate 4,000+ Candidates", href: "https://tellmystory.in/nee-yosi-effect-how-a-chennai-teen-helped-voters-navigate-4000-candidates/" },
  { brand: "LinkedIn", title: "Girish Mathrubootham (Freshworks) — featured NeeYosi.com on LinkedIn", href: null },
];

const MediaDetail = () => (
  <DetailSection id="media" title="Media Coverage" bg={T.cream}>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 16,
      }}
    >
      {MEDIA.map((m) => {
        const Tag = m.href ? "a" : "div";
        const extra = m.href ? { href: m.href, target: "_blank", rel: "noopener noreferrer" } : {};
        return (
          <Tag
            key={m.title}
            {...extra}
            style={{
              display: "block",
              padding: 22,
              backgroundColor: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: 2,
              textDecoration: "none",
              color: T.ink,
              transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 10px 30px -12px rgba(107, 31, 44, 0.25)";
              e.currentTarget.style.borderColor = T.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.borderColor = T.border;
            }}
          >
            <div
              style={{
                fontFamily: T.display,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: T.primary,
                marginBottom: 10,
              }}
            >
              {m.brand}
            </div>
            <div
              style={{
                fontFamily: T.display,
                fontSize: 16,
                fontWeight: 600,
                lineHeight: 1.35,
                color: T.ink,
              }}
            >
              {m.title}
            </div>
            {m.href && (
              <div
                style={{
                  marginTop: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontFamily: T.body,
                  fontSize: 13,
                  fontWeight: 500,
                  color: T.primary,
                }}
              >
                Read article <ArrowUpRight size={13} />
              </div>
            )}
          </Tag>
        );
      })}
    </div>
  </DetailSection>
);

/* --- NeeYosi detail --- */
const NeeYosiDetail = () => (
  <section
    id="neeyosi"
    style={{
      padding: "88px 0",
      backgroundColor: T.primary,
      backgroundImage: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDk} 100%)`,
      color: T.cream,
    }}
  >
    <Container>
      <div
        style={{
          display: "inline-block",
          marginBottom: 32,
          borderBottom: `3px solid ${T.accent}`,
          paddingBottom: 8,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: T.display,
            fontSize: "clamp(26px, 3vw, 34px)",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: T.cream,
          }}
        >
          NeeYosi.com
        </h2>
      </div>
      <p
        style={{
          margin: 0,
          maxWidth: 760,
          fontFamily: T.body,
          fontSize: 18,
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.86)",
        }}
      >
        A nonpartisan Tamil–English voter-information platform. Built and
        launched solo. Turns election affidavits and public records into clear
        candidate profiles any voter can read.
      </p>
      <div
        style={{
          marginTop: 36,
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 24,
          maxWidth: 560,
        }}
      >
        {[
          ["120K+", "Users"],
          ["4,000+", "Candidates"],
          ["234", "Constituencies"],
        ].map(([n, l]) => (
          <div key={l}>
            <div
              style={{
                fontFamily: T.display,
                fontSize: 36,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: T.accent,
              }}
            >
              {n}
            </div>
            <div
              style={{
                marginTop: 4,
                fontFamily: T.mono,
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.72)",
              }}
            >
              {l}
            </div>
          </div>
        ))}
      </div>
      <a
        href="https://neeyosi.com/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="neeyosi-visit-link"
        style={{
          marginTop: 36,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 26px",
          backgroundColor: T.accent,
          color: T.ink,
          fontFamily: T.display,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          textDecoration: "none",
          borderRadius: 2,
        }}
      >
        Visit NeeYosi.com <ArrowUpRight size={14} />
      </a>
    </Container>
  </section>
);

/* --- Internships detail (CSK + MLA) --- */
const INTERNSHIPS = [
  {
    when: "Jul 2026 – present",
    title: "Legislative Office Intern",
    org: "Office of the MLA for Singanallur · K. S. Sri Giri Prasath",
    body: "Invited after NeeYosi came to the MLA's attention. Supporting constituency records and public communication.",
  },
  {
    when: "May 2025",
    title: "Sports Analytics Intern",
    org: "Chennai Super Kings · IPL",
    body: "Built a Python computer-vision model that classifies deliveries from net-practice footage by length.",
  },
];

const InternshipsDetail = () => (
  <DetailSection id="internships" title="Internships">
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 20,
      }}
    >
      {INTERNSHIPS.map((e) => (
        <div
          key={e.title}
          style={{
            padding: 24,
            backgroundColor: T.surface,
            border: `1px solid ${T.border}`,
            borderLeft: `4px solid ${T.primary}`,
            borderRadius: 2,
          }}
        >
          <div
            style={{
              fontFamily: T.mono,
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: T.primary,
              fontWeight: 700,
            }}
          >
            {e.when}
          </div>
          <h3
            style={{
              margin: "10px 0 4px",
              fontFamily: T.display,
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: T.ink,
            }}
          >
            {e.title}
          </h3>
          <div
            style={{
              fontFamily: T.body,
              fontSize: 14,
              color: T.text2,
              fontWeight: 500,
            }}
          >
            {e.org}
          </div>
          <p
            style={{
              margin: "14px 0 0",
              fontFamily: T.body,
              fontSize: 15,
              lineHeight: 1.6,
              color: T.text2,
            }}
          >
            {e.body}
          </p>
        </div>
      ))}
    </div>
  </DetailSection>
);

/* ============================================================
   FOOTER — email · LinkedIn · NeeYosi.com
============================================================ */
const Footer = () => (
  <footer
    id="contact"
    style={{
      padding: "40px 0",
      backgroundColor: T.ink,
      color: T.cream,
    }}
  >
    <Container>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "20px 40px",
        }}
      >
        <div
          style={{
            fontFamily: T.mono,
            fontSize: 12,
            letterSpacing: "0.08em",
            color: "rgba(255,255,255,0.6)",
          }}
        >
          © {new Date().getFullYear()} Kapil Bhaskar · Chennai, India
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "14px 28px",
            alignItems: "center",
          }}
        >
          <a
            href="mailto:KapilBhaskar1451@gmail.com"
            data-testid="footer-email"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: T.body,
              fontSize: 14,
              color: T.cream,
              textDecoration: "none",
            }}
          >
            <Mail size={16} /> KapilBhaskar1451@gmail.com
          </a>
          <a
            href="https://www.linkedin.com/in/kapil-bhaskar-2572aa291/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-linkedin"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: T.body,
              fontSize: 14,
              color: T.cream,
              textDecoration: "none",
            }}
          >
            <Linkedin size={16} /> LinkedIn
          </a>
          <a
            href="https://neeyosi.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-neeyosi"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: T.body,
              fontSize: 14,
              color: T.cream,
              textDecoration: "none",
            }}
          >
            <ExternalLink size={16} /> NeeYosi.com
          </a>
        </div>
      </div>
    </Container>
  </footer>
);

/* ============================================================
   PAGE
============================================================ */
const Home = () => (
  <div
    style={{
      fontFamily: T.body,
      backgroundColor: T.cream,
      color: T.text,
      minHeight: "100vh",
    }}
  >
    <style>{`
      html { scroll-behavior: smooth; }
      body { background: ${T.cream}; }
      a:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 3px; border-radius: 2px; }

      /* --- Mobile tuning --- */
      @media (max-width: 900px) {
        .ref-container  { max-width: 720px !important; }
        .hero-grid { grid-template-columns: 1fr !important; gap: 14px !important; justify-items: center; }
        .ref-grid  { grid-template-columns: repeat(2, 1fr) !important; }
      }
      @media (max-width: 560px) {
        .ref-container  { max-width: 100% !important; padding: 0 16px !important; }
        .ref-grid  { grid-template-columns: 1fr !important; }
        /* Shorter stacked tiles on small screens */
        .ref-tile  { aspect-ratio: 1 / 0.3 !important; min-height: 108px !important; }
      }

      /* --- Touch devices: no hover, so reveal description inline below title --- */
      @media (hover: none) {
        .ref-tile { aspect-ratio: auto !important; min-height: 118px !important; padding: 14px 14px !important; }
        .ref-tile-illus { opacity: 1 !important; transform: none !important; }
        .ref-tile-label { opacity: 1 !important; }
        .ref-tile-desc  {
          opacity: 1 !important;
          transform: none !important;
          position: static !important;
          display: block !important;
          padding: 6px 0 0 !important;
          color: var(--kb-text2, #5a4a3f) !important;
          font-weight: 400 !important;
          font-size: 11.5px !important;
        }
      }
    `}</style>
    <Hero />
    <ReferenceGrid />
    <AwardsDetail />
    <ResearchDetail />
    <MediaDetail />
    <NeeYosiDetail />
    <InternshipsDetail />
    <Footer />
  </div>
);

export default Home;
